import React from 'react';

import Advantage from 'pages/Landings/components/Advantage';
import Advantages from 'pages/Landings/components/Advantages';
import {
    CodeIcon,
    CorrelationIcon,
    ImageIcon,
    SecurityAndAutomationSdkIcon,
    StarsIcon
} from 'pages/Landings/components/Icons';
import Paragraph from 'pages/Landings/components/Paragraph';
import TopicContent from 'pages/Landings/components/TopicContent';
import TopicHeading from 'pages/Landings/components/TopicHeading';
import TopicTemplate from 'pages/Landings/components/TopicTemplate';

const SecurityAutomationSdk: React.FC = () => {
    return (
        <TopicTemplate
            heading={
                <TopicHeading
                    title="Vulnerability Intelligence SDK"
                    description="Product-Ready Context from 200+ Sources"
                    illustration={<SecurityAndAutomationSdkIcon />}
                />
            }
            content={
                <TopicContent>
                    <Paragraph
                        primary="Product-Ready Context from 200+ Sources"
                        secondary="Vulners Vulnerability Intelligence API gives product teams and corporate security engineers a direct line to high-signal, correlated data—so your features reflect what’s actually risky now, not just static scores. One API returns normalized context (metrics, exploits, KEV, patches, references) your UI and automations can use immediately."
                    />
                    <Paragraph
                        primary="What’s Unique: Source Depth You Won’t Find Elsewhere"
                        secondary={
                            'We combine broad coverage with hard-to-get signals. Vulners runs first-party PoC collectors on GitHub and Gitee with smart de-duplication/noise filtering, then cross-links those findings with KEV and standard metrics. You’re drawing from 200+ security advisory sources and millions of entries—kept fresh and product-ready.'
                        }
                    />
                    <Paragraph
                        primary="Representative Source Families (examples)"
                        secondary={
                            <>
                                <ul>
                                    <li>
                                        {
                                            'Vendor Advisories: Red Hat, Oracle, F5, Check Point—pulled and normalized so vendor specifics become consistent downstream.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Linux Distributions: Ubuntu USN, SUSE/openSUSE, Oracle Linux—distribution-native advisories for server fleets and containers.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Blogs & Security News: The Hacker News, Schneier on Security, HackRead, Qualys Blog—context and emerging-threat narrative, unified with CVEs.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Exploit Sources: Exploit-DB, Metasploit, Packet Storm, Seebug, plus GitHub/Gitee PoCs—correlated to CVEs with timelines and references.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'CVE & Catalogs: CVE List, NVD, CISA KEV, and Vulnrichment—authoritative identifiers with enrichment and “exploited-in-the-wild” signals.'
                                        }
                                    </li>
                                    <li>
                                        {
                                            'Windows/Microsoft Sources: MSRC, MSKB, Microsoft Security and Defender (MMPC) feeds—Patch Tuesday to threat intel in one place.'
                                        }
                                    </li>
                                </ul>
                                {'Snapshot of scale:'}
                                <br />
                                <strong>{'5M+ advisories & articles'}</strong>
                                <br />
                                <strong>{'218+ vendor/news sources'}</strong>
                            </>
                        }
                    />
                    <Paragraph
                        primary="Core Capabilities You Plug In"
                        secondary={
                            <ul>
                                <li>
                                    {
                                        'Intelligence (single-call enrichment): CVSS (with vectors), EPSS, KEV, CWE, patches, exploit refs, and Vulners AI Risk—normalized JSON for tooltips, drill-downs, and “fix-first” queues.'
                                    }
                                </li>
                                <li>
                                    {
                                        'Assessment (map assets to CVEs): Send product/version or asset lists; get accurate matches fast enough for CI/CD and estate-wide checks.'
                                    }
                                </li>
                                <li>
                                    {
                                        'Exploits & KEV (what’s actively dangerous): Custom GitHub/Gitee PoC parsing + Exploit-DB/Metasploit/Packet Storm/Seebug and KEV—so you can flag “exploited + present here,” show timelines/PoCs, and reorder backlogs by reality.'
                                    }
                                </li>
                                <li>
                                    {
                                        'Alerts (push what matters): Subscriptions by product, severity, keywords/tags (RCE, zero-day) via webhook/email.'
                                    }
                                </li>
                                <li>
                                    {
                                        'Datasets (sync at scale): Archive exports of the graph-linked corpus with add/update timestamps for analytics and reproducibility.'
                                    }
                                </li>
                                <li>
                                    {
                                        'MCP (optional for agents): Let copilots fetch live CVE context on demand—today’s CVE with today’s facts.'
                                    }
                                </li>
                            </ul>
                        }
                    />
                </TopicContent>
            }
            advantages={
                <Advantages title={'Why Vulners? Built for Builders'}>
                    <Advantage
                        icon={<ImageIcon />}
                        title="Content advantage"
                        description="High-signal sources (including first-party GitHub/Gitee PoCs) you won’t get by stitching public feeds alone."
                    />
                    <Advantage
                        icon={<CorrelationIcon />}
                        title="Correlation that matters"
                        description="Exploit/PoC evidence and KEV aligned with metrics and vendor/distribution advisories—so severity maps to active risk."
                    />
                    <Advantage
                        icon={<StarsIcon />}
                        title="Proven breadth & freshness"
                        description="Continuously updated database from 200+ sources; typical CVE-to-exploit windows measured in hours, not days—making exploit-aware features actually useful."
                    />
                    <Advantage
                        icon={<CodeIcon />}
                        title="Developer-first integration"
                        description="Clean JSON + SDKs, stable fields, and a 99.9% SLA — plus free engineering support — so you ship fast without maintaining parsers or feeds."
                    />
                </Advantages>
            }
        />
    );
};

export default SecurityAutomationSdk;
